import { createTheme, alpha } from '@mui/material/styles';

/**
 * The AiViz design system, taken from the AiTime RFI response document:
 * white page, teal headings, solid teal table headers, flat cards with a thin
 * border, and small uppercase pill badges. Nothing glows and nothing is
 * translucent -- the look is print, not glass.
 *
 * Spacing is tighter than the document's, deliberately. That is a report
 * people read once; this is a tool people work in all day, so lists stay
 * dense enough to scan.
 */

/** Brand constants. Teal is sampled from the document; the greens from the logo. */
const brand = {
  teal: '#0F7A6B',
  tealDark: '#0A5A4F',
  tealLight: '#17A08C',
  logoGreen: '#00A383',
  slate: '#4A5A66',
  amber: '#B9761C',
};

const commonTypography = {
  fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  h1: { fontSize: '1.75rem', fontWeight: 700, letterSpacing: '-0.02em' },
  h2: { fontSize: '1.375rem', fontWeight: 700, letterSpacing: '-0.01em' },
  h3: { fontSize: '1.125rem', fontWeight: 700, letterSpacing: '-0.01em' },
  h4: { fontSize: '1rem', fontWeight: 700 },
  h5: { fontSize: '0.9375rem', fontWeight: 700 },
  h6: { fontSize: '0.875rem', fontWeight: 700 },
  body1: { fontSize: '0.875rem', fontWeight: 400 },
  body2: { fontSize: '0.8125rem', fontWeight: 400 },
  caption: { fontSize: '0.75rem', fontWeight: 400 },
  // The document's small-caps labels above tables and stat tiles.
  overline: {
    fontSize: '0.6875rem',
    fontWeight: 700,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    lineHeight: 1.6,
  },
  button: { fontWeight: 600, textTransform: 'none' },
};

const commonShape = { borderRadius: 6 };

const commonComponents = (palette) => ({
  MuiCssBaseline: {
    styleOverrides: {
      body: {
        backgroundColor: palette.background.default,
        WebkitFontSmoothing: 'antialiased',
      },
      'input:disabled, select:disabled, textarea:disabled': {
        backgroundColor: 'transparent !important',
      },
    },
  },
  MuiPaper: {
    styleOverrides: {
      // MUI tints dark-mode surfaces with a gradient; the document's surfaces are flat.
      root: { backgroundImage: 'none' },
    },
  },
  MuiCard: {
    styleOverrides: {
      root: {
        backgroundImage: 'none',
        borderRadius: 8,
        border: `1px solid ${palette.divider}`,
        boxShadow: 'none',
      },
    },
  },
  MuiCardHeader: {
    styleOverrides: {
      root: { padding: '14px 16px 0' },
      action: { margin: 0 },
    },
  },
  MuiCardContent: {
    styleOverrides: {
      root: { padding: 16, '&:last-child': { paddingBottom: 16 } },
    },
  },
  MuiButton: {
    defaultProps: { disableElevation: true },
    styleOverrides: {
      root: { borderRadius: 6, padding: '6px 16px' },
      sizeSmall: { padding: '4px 12px' },
      outlined: { borderColor: palette.divider },
    },
  },
  MuiChip: {
    styleOverrides: {
      root: {
        borderRadius: 999,
        fontWeight: 700,
        fontSize: '0.6875rem',
        letterSpacing: '0.04em',
      },
      sizeSmall: { height: 22 },
    },
  },
  MuiTableCell: {
    styleOverrides: {
      root: {
        padding: '8px 12px',
        borderBottom: `1px solid ${palette.divider}`,
        fontSize: '0.8125rem',
      },
      // The document's signature: a solid teal band with small white caps.
      head: {
        backgroundColor: palette.brand.headerBg,
        color: palette.brand.headerText,
        fontWeight: 700,
        fontSize: '0.6875rem',
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
        borderBottom: 'none',
        whiteSpace: 'nowrap',
      },
    },
  },
  MuiTableRow: {
    styleOverrides: {
      root: {
        '&:nth-of-type(even):not(.MuiTableRow-head)': {
          backgroundColor: palette.brand.rowAlt,
        },
        '&:hover:not(.MuiTableRow-head)': { backgroundColor: palette.brand.tint },
      },
    },
  },
  MuiDataGrid: {
    styleOverrides: {
      root: {
        border: `1px solid ${palette.divider}`,
        borderRadius: 8,
        fontSize: '0.8125rem',
        '--DataGrid-rowBorderColor': palette.divider,
      },
      columnHeader: {
        backgroundColor: palette.brand.headerBg,
        color: palette.brand.headerText,
        '--DataGrid-containerBackground': palette.brand.headerBg,
      },
      columnHeaderTitle: {
        fontWeight: 700,
        fontSize: '0.6875rem',
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
      },
      // The sort and menu arrows are drawn in the header's own colour.
      iconButtonContainer: { color: palette.brand.headerText },
      sortIcon: { color: palette.brand.headerText },
      menuIcon: { color: palette.brand.headerText },
      columnSeparator: { color: alpha(palette.brand.headerText, 0.3) },
      row: {
        '&:nth-of-type(even)': { backgroundColor: palette.brand.rowAlt },
        '&:hover': { backgroundColor: palette.brand.tint },
      },
      cell: { borderBottom: `1px solid ${palette.divider}` },
      footerContainer: { borderTop: `1px solid ${palette.divider}` },
    },
  },
  MuiTabs: {
    styleOverrides: {
      indicator: { height: 2, backgroundColor: palette.primary.main },
    },
  },
  MuiTab: {
    styleOverrides: {
      root: {
        textTransform: 'none',
        fontWeight: 600,
        fontSize: '0.875rem',
        minHeight: 44,
      },
    },
  },
  MuiTextField: {
    defaultProps: { variant: 'outlined', size: 'small' },
  },
  MuiOutlinedInput: {
    styleOverrides: {
      root: {
        borderRadius: 6,
        '& .MuiOutlinedInput-notchedOutline': { borderColor: palette.divider },
        '&:hover .MuiOutlinedInput-notchedOutline': {
          borderColor: alpha(palette.text.primary, 0.3),
        },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
          borderWidth: 1,
          borderColor: palette.primary.main,
        },
        '&.Mui-disabled .MuiOutlinedInput-notchedOutline': {
          borderColor: palette.divider,
          borderStyle: 'dashed',
        },
      },
    },
  },
  MuiDialog: {
    styleOverrides: {
      paper: {
        borderRadius: 8,
        backgroundImage: 'none',
        border: `1px solid ${palette.divider}`,
      },
    },
  },
  MuiDialogTitle: {
    styleOverrides: {
      root: { fontSize: '1.125rem', fontWeight: 700, padding: '16px 20px' },
    },
  },
  MuiAppBar: {
    styleOverrides: {
      root: {
        backgroundImage: 'none',
        backgroundColor: palette.background.paper,
        color: palette.text.primary,
        borderBottom: `1px solid ${palette.divider}`,
        boxShadow: 'none',
      },
    },
  },
  MuiDrawer: {
    styleOverrides: {
      paper: {
        backgroundImage: 'none',
        backgroundColor: palette.background.paper,
        borderRight: `1px solid ${palette.divider}`,
      },
    },
  },
  MuiAlert: {
    styleOverrides: {
      root: { borderRadius: 6, fontSize: '0.8125rem' },
    },
  },
  MuiTooltip: {
    styleOverrides: {
      tooltip: { borderRadius: 6, fontSize: '0.75rem' },
    },
  },
  MuiLinearProgress: {
    styleOverrides: {
      root: { borderRadius: 999, height: 6 },
    },
  },
  MuiDivider: {
    styleOverrides: {
      root: { borderColor: palette.divider },
    },
  },
});

export const createDarkTheme = () => {
  const palette = {
    mode: 'dark',
    primary: { main: '#2FB39C', light: '#53CBB4', dark: '#17806E', contrastText: '#06201C' },
    secondary: { main: '#8FA3AE', light: '#AFC0C9', dark: '#6B7F8A' },
    success: { main: '#34A96F' },
    warning: { main: '#D79433' },
    error: { main: '#D9594C' },
    info: { main: '#4B8FD0' },
    background: { default: '#0C1716', paper: '#132220' },
    text: { primary: '#E6EEEC', secondary: '#9DB0AC', disabled: '#64756F' },
    divider: 'rgba(255, 255, 255, 0.10)',
    // Surfaces that carry the document's look: table bands, striped rows, tints.
    brand: {
      headerBg: '#17403A',
      headerText: '#D8EAE6',
      rowAlt: 'rgba(255, 255, 255, 0.03)',
      tint: 'rgba(47, 179, 156, 0.12)',
      rule: '#2FB39C',
    },
    /**
     * Kept under its old name so components not yet reworked keep rendering.
     * These are now opaque surfaces, not glass -- blur is '0px' on purpose.
     */
    glass: {
      background: '#132220',
      backgroundHover: '#182B28',
      border: 'rgba(255, 255, 255, 0.10)',
      blur: '0px',
      blurHeavy: '0px',
    },
  };

  return createTheme({
    palette,
    typography: commonTypography,
    shape: commonShape,
    components: commonComponents(palette),
  });
};

export const createLightTheme = () => {
  const palette = {
    mode: 'light',
    primary: {
      main: brand.teal,
      light: brand.tealLight,
      dark: brand.tealDark,
      contrastText: '#FFFFFF',
    },
    secondary: { main: brand.slate, light: '#64748B', dark: '#33414B' },
    success: { main: '#1E8E5A' },
    warning: { main: brand.amber },
    error: { main: '#C0392B' },
    info: { main: '#2A6FB0' },
    background: { default: '#F4F6F6', paper: '#FFFFFF' },
    text: { primary: '#1B2524', secondary: '#5B6B68', disabled: '#9AA8A5' },
    divider: '#E1E8E7',
    brand: {
      headerBg: brand.teal,
      headerText: '#FFFFFF',
      rowAlt: '#F7FAFA',
      tint: '#E8F2F0',
      rule: brand.teal,
    },
    glass: {
      background: '#FFFFFF',
      backgroundHover: '#F7FAFA',
      border: '#E1E8E7',
      blur: '0px',
      blurHeavy: '0px',
    },
  };

  return createTheme({
    palette,
    typography: commonTypography,
    shape: commonShape,
    components: commonComponents(palette),
  });
};

/** The accent used for charts and the logo mark, outside the MUI palette. */
export const BRAND = brand;
